import { Box, CircularProgress, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { FC, useContext, useState } from "react";
import { useAppStore } from "../../../../../../../../hooks/useStores";
import { sortById } from "../../../../../../../../utils/index.ts";
import useImageUtils from "../../../../../../../../utils/useImageUtils";
import { DesignEditorContext } from "../../../../../../../contexts/DesignEditor.tsx";
import { BrandDefinition } from "../../../../../../../models/brandDefinition";
import DuplicateBrandPopup from "../../../../DesignEditorV2/DuplicateBrandPopup/DuplicateBrandPopup.tsx";
import styles from "./BrandItem.module.scss";
import BrandModalOverlay from "./BrandModalOverlay";

interface BrandItemProps {
	isBrandTemplateEditor: boolean | undefined;
	brand: BrandDefinition;
	key: number;
	onClick: (brand: BrandDefinition) => void;
	selectedBrand: BrandDefinition | null;
	handleShowBrand: () => void;
	loadBrands: (forcefetchBrands?: boolean) => void;
	setIsViewIconClicked: (value: boolean) => void;
	setActiveBrand: (brand: BrandDefinition) => void;
	activeBrand: BrandDefinition | null;
	isBrandFromPublicOrg?: boolean;
}

const BrandItem: FC<BrandItemProps> = ({
	isBrandTemplateEditor,
	brand,
	key,
	onClick,
	selectedBrand,
	handleShowBrand,
	loadBrands,
	setIsViewIconClicked,
	setActiveBrand,
	activeBrand,
	isBrandFromPublicOrg,
}) => {
	const { isS3Uri, convertS3UriToHttpsCloudFrontUrl } = useImageUtils();
	const { brandsDefinitionStore, campaignStore } = useAppStore();
	const brandImage = sortById(brand?.logos).find((logo) => logo.src)?.src ?? "";
	const src = isS3Uri(brandImage) ? convertS3UriToHttpsCloudFrontUrl(brandImage) : brandImage;
	const { isPopupView } = useContext(DesignEditorContext);
	const [openDuplicateBrandModal, setOpenDuplicateBrandModal] = useState<boolean>(false);
	const [isBrandDeleting, setIsBrandDeleting] = useState(false);

	const handleDeleteBrand = async () => {
		setIsBrandDeleting(true);
		try {
			await brandsDefinitionStore.handleDeleteBrand(brand);
			loadBrands();
		} catch (error) {
			console.error("Delete brand failed:", error);
		} finally {
			setIsBrandDeleting(false);
		}
	};

	const handleShowEdit = () => {
		setIsViewIconClicked(true);
		handleShowBrand();
		onClick(brand);
		brandsDefinitionStore.formErrors.invalidName = false;
	};

	const handleDuplicate = () => {
		brandsDefinitionStore.formErrors.invalidName = false;
		setIsViewIconClicked(true);
		setOpenDuplicateBrandModal(true);
	};
	return (
		<Box
			className={clsx(styles.itemWrapper, {
				[styles.selectedBrand]:
					(!campaignStore.selectedTemplate &&
						brandsDefinitionStore.globalSelectedBrand &&
						brand.id === brandsDefinitionStore.globalSelectedBrand.id) ||
					(campaignStore.selectedTemplate?.is_branded_template &&
						brandsDefinitionStore.globalSelectedBrand &&
						brand.id === brandsDefinitionStore.globalSelectedBrand.id) ||
					(campaignStore.selectedTemplate?.is_branded_template && brand.id === activeBrand?.id),
				[styles.disabled]: brandsDefinitionStore.isApplyingBrandConfigurations,
			})}
			key={key}
			onClick={(e) => {
				if (openDuplicateBrandModal) {
					e.stopPropagation();
					return;
				}
				setIsViewIconClicked(false);
				onClick(brand);
				setActiveBrand(brand);
				brandsDefinitionStore.setProperty("globalSelectedBrand", brand);
				if (campaignStore.selectedTemplate) campaignStore.selectedTemplate.is_branded_template = true;
			}}
		>
			<Box className={styles.content}>
				<Box className={styles.logo}>
					<img src={src} />
				</Box>
				<Typography>{brand.name}</Typography>
			</Box>

			{selectedBrand && brand.id === selectedBrand.id && brandsDefinitionStore.isApplyingBrandConfigurations && (
				<>
					<Box className={styles.blurLayer} />
					<CircularProgress size={24} className={styles.loader} />
				</>
			)}

			{!isPopupView && (
				<BrandModalOverlay
					brand={brand}
					isBrandTemplateEditor={isBrandTemplateEditor}
					isBrandFromPublicOrg={isBrandFromPublicOrg}
					onShowEdit={handleShowEdit}
					onDuplicate={handleDuplicate}
					onDelete={handleDeleteBrand}
					isDeleting={isBrandDeleting}
				/>
			)}
			<DuplicateBrandPopup
				brand={brand}
				loadBrands={loadBrands}
				setOpenDuplicateBrandModal={setOpenDuplicateBrandModal}
				openDuplicateBrandModal={openDuplicateBrandModal}
			/>
		</Box>
	);
};
const ObservedComponent = observer(BrandItem);
export default ObservedComponent;
